import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Typography, 
    Container, 
    Paper, 
    Grid, 
    Stack, 
    CircularProgress,
    useTheme,
    alpha,
    Button,
    Snackbar,
    Alert
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip as RechartsTooltip, 
    ResponsiveContainer,
    LineChart,
    Line,
    Legend
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import careService from '../services/careService';
import { CareSLAReportDTO } from '../types/crm.types';

const CareReportsPage: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState<CareSLAReportDTO[]>([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });

    useEffect(() => {
        const fetchReports = async () => {
            try {
                const data = await careService.getSLAReports();
                setReports(data);
            } catch (error) {
                console.error('Failed to fetch reports', error);
            } finally {
                setLoading(false);
            }
        };

        fetchReports();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth={false} sx={{ py: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight="800" color="text.primary">
                        Performance & SLA Analytics
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Analyze resolution times, SLA compliance, and service performance trends.
                    </Typography>
                </Box>
                <Button 
                    variant="outlined" 
                    startIcon={<FileDownloadIcon />} 
                    onClick={() => setSnackbar({ open: true, message: 'Exporting report as PDF...' })}
                    sx={{ borderRadius: 2, fontWeight: '700' }}
                >
                    Export Report
                </Button>
            </Box>

            <Grid container spacing={4}>
                {/* SLA Compliance Chart */}
                <Grid item xs={12} lg={8}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 4, height: 400 }}>
                        <Typography variant="h6" fontWeight="700" sx={{ mb: 3 }}>SLA Compliance Trend (%)</Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={reports}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.divider, 0.5)} />
                                <XAxis dataKey="month" axisLine={false} tickLine={false} />
                                <YAxis axisLine={false} tickLine={false} />
                                <RechartsTooltip 
                                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: theme.shadows[3] }}
                                />
                                <Legend />
                                <Bar dataKey="targetMet" name="Target Met" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
                                <Bar dataKey="targetBreached" name="Breached" fill={theme.palette.error.main} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Avg Resolution Time Chart */}
                <Grid item xs={12} lg={4}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 4, height: 400 }}>
                        <Typography variant="h6" fontWeight="700" sx={{ mb: 3 }}>Avg. Resolution Time (Hrs)</Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={reports}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.divider, 0.5)} />
                                <XAxis dataKey="month" axisLine={false} tickLine={false} />
                                <YAxis axisLine={false} tickLine={false} />
                                <RechartsTooltip 
                                    contentStyle={{ borderRadius: 8, border: 'none', boxShadow: theme.shadows[3] }}
                                />
                                <Line 
                                    type="monotone" 
                                    dataKey="avgResolutionTime" 
                                    name="Hours" 
                                    stroke={theme.palette.primary.main} 
                                    strokeWidth={3} 
                                    dot={{ r: 6 }} 
                                    activeDot={{ r: 8 }} 
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Key Metrics Summary */}
                <Grid item xs={12}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.success.main, 0.2), borderRadius: 3 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <CheckCircleOutlineIcon color="success" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h5" fontWeight="800">92.5%</Typography>
                                        <Typography variant="body2" color="text.secondary">Overall SLA Compliance</Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.info.main, 0.2), borderRadius: 3 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <SpeedIcon color="info" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h5" fontWeight="800">4.2 Hrs</Typography>
                                        <Typography variant="body2" color="text.secondary">Avg. Resolution Speed</Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.2), borderRadius: 3 }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />
                                    <Box>
                                        <Typography variant="h5" fontWeight="800">78%</Typography>
                                        <Typography variant="body2" color="text.secondary">Engineer Utilization</Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={3000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CareReportsPage;
